export type StockStatus = 'available' | 'consult' | 'out_of_stock';

export interface ProductProps {
  id: string;
  name: string;
  slug: string;
  brand: string;
  model: string;
  categoryId: string;
  categoryName: string;
  subcategoryId?: string;
  subcategorySlug?: string;
  subcategoryName?: string;
  shortDescription: string;
  longDescription: string;
  specifications: Record<string, string>;
  interfaces: string[];
  imageUrl: string;
  images?: string[];
  datasheetUrl?: string;
  isFeatured?: boolean;
  stockStatus: StockStatus;
  // Especificaciones técnicas estructuradas para filtrado avanzado de hardware
  operatingSystem?: string;
  screenSize?: string;
  ram?: string;
  storage?: string;
  processor?: string;
  scannerType?: string;
  printTechnology?: string;
  connectivity?: string[];
  isPromotion?: boolean;
  promotionTitle?: string | null;
  promotionImageUrl?: string | null;
  promotionImages?: string[];
}

export class ProductEntity {
  constructor(private readonly props: ProductProps) {
    if (!props.id) throw new Error('El ID del producto es obligatorio');
    if (!props.name) throw new Error('El nombre del producto es obligatorio');
    if (!props.slug) throw new Error('El slug del producto es obligatorio');
    if (!props.brand) throw new Error('La marca del producto es obligatoria');
  }

  get id(): string {
    return this.props.id;
  }

  get name(): string {
    return this.props.name;
  }

  get slug(): string {
    return this.props.slug;
  }

  get brand(): string {
    return this.props.brand;
  }

  get model(): string {
    return this.props.model;
  }

  get categoryId(): string {
    return this.props.categoryId;
  }

  get categoryName(): string {
    return this.props.categoryName;
  }

  get subcategoryId(): string | undefined {
    return this.props.subcategoryId;
  }

  get subcategorySlug(): string | undefined {
    return this.props.subcategorySlug;
  }

  get subcategoryName(): string | undefined {
    return this.props.subcategoryName;
  }

  get shortDescription(): string {
    return this.props.shortDescription;
  }

  get longDescription(): string {
    return this.props.longDescription;
  }

  get specifications(): Record<string, string> {
    return { ...this.props.specifications };
  }

  get interfaces(): string[] {
    return [...this.props.interfaces];
  }

  get imageUrl(): string {
    return this.props.imageUrl || this.props.images?.[0] || '';
  }

  get images(): string[] {
    const rawImages =
      this.props.images && this.props.images.length > 0
        ? this.props.images
        : this.props.imageUrl
        ? [this.props.imageUrl]
        : [];

    const seen = new Set<string>();
    const unique: string[] = [];
    for (const url of rawImages) {
      if (!url || typeof url !== 'string') continue;
      const clean = url.trim();
      const key = clean.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(clean);
      }
    }

    return unique.length > 0 ? unique : rawImages;
  }

  get datasheetUrl(): string | undefined {
    return this.props.datasheetUrl;
  }

  get isFeatured(): boolean {
    return !!this.props.isFeatured;
  }

  get stockStatus(): StockStatus {
    return this.props.stockStatus;
  }

  get operatingSystem(): string | undefined {
    return this.props.operatingSystem;
  }

  get screenSize(): string | undefined {
    return this.props.screenSize;
  }

  get ram(): string | undefined {
    return this.props.ram;
  }

  get storage(): string | undefined {
    return this.props.storage;
  }

  get processor(): string | undefined {
    return this.props.processor;
  }

  get scannerType(): string | undefined {
    return this.props.scannerType;
  }

  get printTechnology(): string | undefined {
    return this.props.printTechnology;
  }

  get connectivity(): string[] {
    return this.props.connectivity ? [...this.props.connectivity] : [];
  }

  get isPromotion(): boolean {
    return !!this.props.isPromotion;
  }

  get promotionTitle(): string | null | undefined {
    return this.props.promotionTitle;
  }

  get promotionImageUrl(): string | null | undefined {
    return this.props.promotionImageUrl;
  }

  get promotionImages(): string[] {
    return this.props.promotionImages ? [...this.props.promotionImages] : [];
  }

  get promoAndProductImages(): string[] {
    const list = [...this.images];
    if (this.props.promotionImageUrl && !list.includes(this.props.promotionImageUrl)) {
      list.push(this.props.promotionImageUrl);
    }
    if (this.props.promotionImages) {
      for (const pi of this.props.promotionImages) {
        if (!list.includes(pi)) list.push(pi);
      }
    }
    return list;
  }

  toJSON(): ProductProps {
    return {
      ...this.props,
      connectivity: this.connectivity,
    };
  }
}
